import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Cloud, Wind, AlertTriangle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export function StormWatchWidget() {
  const [weather, setWeather] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWeather();
  }, []);

  const fetchWeather = async () => {
    setLoading(true);
    try {
      const location = "Chicago"; // TODO: Get from org profile
      const { data, error } = await supabase.functions.invoke('get-weather-forecast', {
        body: { location, days: 5 }
      });

      if (error) throw error;
      setWeather(data);
    } catch (error) {
      console.error('Error fetching weather:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !weather) {
    return (
      <Card className="bg-card/50 border-border/50">
        <CardContent className="p-3">
          <div className="flex items-center gap-2">
            <Cloud className="w-4 h-4 text-muted-foreground animate-pulse" />
            <span className="text-xs text-muted-foreground">Loading weather...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Calculate hail risk based on conditions
  const getHailRisk = () => {
    const condition = weather.current.condition.toLowerCase();
    const windSpeed = weather.current.wind_mph;
    const humidity = weather.current.humidity;

    if ((condition.includes('storm') || condition.includes('thunder')) && windSpeed > 20) {
      return { level: 'High', color: 'destructive' as const };
    } else if (condition.includes('rain') && windSpeed > 15) {
      return { level: 'Med', color: 'secondary' as const };
    } else {
      return { level: 'Low', color: 'default' as const };
    }
  };

  const hailRisk = getHailRisk();

  return (
    <Card className="bg-gradient-to-br from-sky-500/10 to-sky-500/5 border-sky-500/20">
      <CardContent className="p-3 space-y-3">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Cloud className="w-4 h-4 text-sky-500" />
            <div className="text-left">
              <p className="text-xs font-medium text-muted-foreground">Local Storm Watch</p>
              <p className="text-lg font-bold text-foreground">{Math.round(weather.current.temp_f)}°F</p>
            </div>
          </div>
          
          <div className="h-8 w-px bg-border/50" />
          
          <div className="flex items-center gap-2">
            <Wind className="w-4 h-4 text-sky-400" />
            <div className="text-left">
              <p className="text-xs text-muted-foreground">Wind</p>
              <p className="text-sm font-semibold text-foreground">{Math.round(weather.current.wind_mph)} mph</p>
            </div>
          </div>
          
          <div className="h-8 w-px bg-border/50" />
          
          <div className="flex items-center gap-2">
            <AlertTriangle className={`w-4 h-4 ${hailRisk.level === 'High' ? 'text-destructive' : hailRisk.level === 'Med' ? 'text-orange-500' : 'text-green-500'}`} />
            <div className="text-left">
              <p className="text-xs text-muted-foreground">Hail Risk</p>
              <Badge variant={hailRisk.color} className="text-xs font-semibold">
                {hailRisk.level}
              </Badge>
            </div>
          </div>
        </div>

        {/* 5-Day Forecast */}
        <div className="pt-2 border-t border-border/50">
          <p className="text-xs font-medium text-muted-foreground mb-2">5-Day Forecast</p>
          <div className="grid grid-cols-5 gap-2">
            {weather.forecast.slice(0, 5).map((day: any, index: number) => (
              <div key={day.date} className="text-center">
                <p className="text-xs text-muted-foreground mb-1">
                  {index === 0 ? 'Today' : new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                </p>
                <img 
                  src={`https:${day.icon}`} 
                  alt={day.condition}
                  className="w-8 h-8 mx-auto"
                />
                <p className="text-xs font-semibold text-foreground">
                  {Math.round(day.max_temp_f)}°
                </p>
                <p className="text-xs text-muted-foreground">
                  {Math.round(day.min_temp_f)}°
                </p>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
